// Markdown-style subset for decorating text blocks - **bold**, *italic*,
// __underline__, ~~strikethrough~~, ==RRGGBB:highlight==. Extends the
// original **bold**-only prior art (exercise-templates/boldText.jsx,
// deliberately "no library, no contentEditable") to the full decoration
// set requested for both Topic and Exercise text blocks, ported identically
// into english-app so a decorated block renders the same on the platform.
// No sanitizer needed anywhere - this only ever emits a fixed set of React
// elements from a regex match, never dangerouslySetInnerHTML.
// The 2-char-delimiter groups (bold/strike/underline/highlight) use a
// "consume anything not starting my own closing delimiter" lookahead
// instead of a plain [^*]+-style exclusion class - a plain exclusion class
// broke as soon as the content contained a literal single instance of that
// delimiter's own character (confirmed live: **bold *and italic* still
// bold** - the outer **'s content class excluded ALL '*' including the
// single ones from the nested *italic*, so it never found its own closing
// ** and a neighbouring single-* alternative wrongly swallowed the nested
// italic's opening marker instead). Only italic (a single '*') still uses
// a plain exclusion class - reciprocal nesting (*italic **bold** italic*)
// is a known, accepted limitation, same class of ambiguity real markdown
// parsers special-case extensively for.
//
// Also known/accepted (flagged by english-app-3e while porting this):
// a mark's closing delimiter sitting directly against another mark's
// closing delimiter with zero separating character - e.g.
// `**bold *italic***` - is ambiguous (reads as `**...**`+stray `*`, not
// `**bold *italic**` + trailing `*`). Not reachable from two genuinely
// separate toolbar selections (there's always at least the marker's own
// closing char between them), only from re-selecting a phrase that already
// ends exactly on another mark's close and wrapping that too. Same
// "flanking rule" class of ambiguity CommonMark spends real complexity
// resolving - not worth it for this deliberately minimal feature.
const MARK_RE = /\*\*((?:(?!\*\*).)+)\*\*|~~((?:(?!~~).)+)~~|__((?:(?!__).)+)__|==([0-9a-fA-F]{3,6}):((?:(?!==).)+)==|\*([^*]+)\*/g;

// Lines starting with "- " form a list block (<ul><li>...) - a separate,
// block-level pass over the whole text BEFORE the inline mark parser below
// (which only ever sees one paragraph/list-item's text at a time). Wraps
// non-list runs in a bare <span> (not <p>) specifically so a caller can
// safely put this inside a <div> even when a <ul> is one of the returned
// siblings - a <ul> nested inside a real <p> is invalid HTML and gets
// silently un-nested by the browser, so every call site renders this into
// a plain container, never a <p>, now that lists exist.
export function renderRichText(text) {
   if (!text) return null;
   const lines = text.split('\n');
   const blocks = [];
   let paragraphLines = [];
   let listItems = null;
   let key = 0;

   const flushParagraph = () => {
      if (paragraphLines.length === 0) return;
      blocks.push(<span key={'p' + key++}>{parseSegment(paragraphLines.join('\n'), { n: 0 })}</span>);
      paragraphLines = [];
   };
   const flushList = () => {
      if (!listItems) return;
      blocks.push(
         <ul key={'ul' + key++} className='rich-text-list'>
            {listItems.map((item, i) => <li key={i}>{parseSegment(item, { n: 0 })}</li>)}
         </ul>
      );
      listItems = null;
   };

   lines.forEach((line) => {
      const m = line.match(/^-\s+(.*)$/);
      if (m) {
         flushParagraph();
         if (!listItems) listItems = [];
         listItems.push(m[1]);
      } else {
         flushList();
         paragraphLines.push(line);
      }
   });
   flushParagraph();
   flushList();

   return blocks;
}

// Recursive: each match's captured inner text is re-parsed (so
// **bold _and italic_** nests correctly) before being wrapped in its
// element and appended, then parsing continues after the marker.
//
// str.matchAll (not a manual MARK_RE.exec()/lastIndex loop) specifically
// because this function recurses into itself for each match's inner text -
// exec()/lastIndex is mutable state on the shared module-level MARK_RE
// object, so a recursive call resetting/advancing it corrupted the outer
// call's own iteration position (the outer loop would re-scan from
// wherever the innermost recursive call left lastIndex) - a real infinite
// loop, confirmed live (crashed the browser tab). matchAll takes its own
// internal copy of the regex per call, so nested calls can never see each
// other's position.
function parseSegment(str, keyRef) {
   const nodes = [];
   let cursor = 0;
   for (const match of str.matchAll(MARK_RE)) {
      if (match.index > cursor) nodes.push(str.slice(cursor, match.index));
      const key = keyRef.n++;
      if (match[1] !== undefined) nodes.push(<strong key={key}>{parseSegment(match[1], keyRef)}</strong>);
      else if (match[2] !== undefined) nodes.push(<s key={key}>{parseSegment(match[2], keyRef)}</s>);
      else if (match[3] !== undefined) nodes.push(<u key={key}>{parseSegment(match[3], keyRef)}</u>);
      else if (match[4] !== undefined) nodes.push(<mark key={key} style={{ background: '#' + match[4] }}>{parseSegment(match[5], keyRef)}</mark>);
      else if (match[6] !== undefined) nodes.push(<em key={key}>{parseSegment(match[6], keyRef)}</em>);
      cursor = match.index + match[0].length;
   }
   if (cursor < str.length) nodes.push(str.slice(cursor));
   return nodes;
}

// Marker pairs the floating toolbar wraps a selection with - shared here so
// the editor and the parser can never drift apart on syntax.
export const RICH_TEXT_MARKERS = {
   bold: ['**', '**'],
   italic: ['*', '*'],
   underline: ['__', '__'],
   strikethrough: ['~~', '~~'],
};
export function highlightMarker(hexColor) {
   const clean = hexColor.replace('#', '');
   return ['==' + clean + ':', '=='];
}
export const HIGHLIGHT_SWATCHES = ['fff59d', 'a5d6a7', '90caf9', 'f48fb1', 'ffcc80'];
